import {cva} from 'platform/shared';
import {match} from 'ts-pattern';

import {AriaRole, ForwardedRef, forwardRef, ReactNode} from 'react';

import {isNil} from 'ramda';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {useFoundationTheme} from '../../hooks/useFoundationTheme';
import {useResponsivePropValue} from '../../hooks/useResponsivePropValue';
import {ValueByDevice} from '../../types/ValueByDevice';
import {cn} from '../../utils/cn';
import {ThemeColorTextPath} from '../../utils/foundationTheme';
import {getTextColorByPath} from '../../utils/getTextColorByPath';

export type TextAlign = 'left' | 'center' | 'right' | 'justify';
export type TextSize = 'large' | 'base' | 'small' | 'xSmall' | 'xxSmall';

const TEXT_SIZE_CLASSES: Record<TextSize, string> = {
  large: 'text-[length:var(--text-size-large)] leading-[var(--text-leading-large)]',
  base: 'text-[length:var(--text-size-base)] leading-[var(--text-leading-base)]',
  small: 'text-[length:var(--text-size-small)] leading-[var(--text-leading-small)]',
  xSmall: 'text-[length:var(--text-size-x-small)] leading-[var(--text-leading-x-small)]',
  xxSmall: 'text-[length:var(--text-size-xx-small)] leading-[var(--text-leading-xx-small)]',
};

const TEXT_ALIGN_CLASSES: Record<TextAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
  justify: 'text-justify',
};

const textVariants = cva(
  '[font-family:var(--font-family-body,Roboto,_sans-serif)] tracking-[var(--letter-spacing-text,0)]',
  {
    variants: {
      weight: {
        default: 'font-[var(--text-weight-default)]',
        alternative: 'font-[var(--text-weight-alternative)]',
      },
      display: {
        block: 'block',
        inline: 'inline',
      },
      strikethrough: {
        true: '[text-decoration:line-through]',
        false: '[text-decoration:none]',
      },
      truncate: {
        true: 'truncate',
        false: '',
      },
      whiteSpace: {
        inherit: '',
        nowrap: 'whitespace-nowrap',
        preWrap: 'whitespace-pre-wrap',
      },
      overflowWrap: {
        anywhere: '[overflow-wrap:anywhere]',
        initial: '[overflow-wrap:initial]',
      },
    },
    defaultVariants: {
      weight: 'default',
      display: 'block',
      strikethrough: false,
      truncate: false,
      whiteSpace: 'inherit',
      overflowWrap: 'initial',
    },
  }
);

export interface TextProps extends TestIdProps {
  // tw-only extension preserving the old styled-based component's implicit className support
  className?: string;
  size?: TextSize | ValueByDevice<TextSize | undefined>;
  color?: ThemeColorTextPath | Nullish;
  inline?: true;
  alternative?: boolean | Nullish;
  strikethrough?: boolean | Nullish;
  children?: string | ReactNode | Nullish;
  noWrap?: boolean;
  preWrap?: boolean;
  role?: AriaRole;
  overflowWrap?: 'anywhere';
  title?: string;
  align?: TextAlign | ValueByDevice<TextAlign>;
  id?: string;
}

const getWhiteSpaceVariant = (props: Pick<TextProps, 'noWrap' | 'preWrap'>) =>
  match([Boolean(props.noWrap), Boolean(props.preWrap)])
    .with([true, false], () => 'nowrap' as const)
    .with([false, true], () => 'preWrap' as const)
    .with([false, false], () => 'inherit' as const)
    .otherwise(() => {
      throw new Error('Cannot use both noWrap and preWrap');
    });

export const Text = forwardRef((props: TextProps, ref: ForwardedRef<HTMLDivElement>) => {
  const theme = useFoundationTheme();
  const align = useResponsivePropValue(props.align);
  const size = useResponsivePropValue(props.size);

  return (
    <div
      ref={ref}
      id={props.id}
      data-testid={suffixTestId('text', props)}
      role={props.role}
      className={cn(
        textVariants({
          weight: props.alternative ? 'alternative' : 'default',
          display: props.inline ? 'inline' : 'block',
          strikethrough: Boolean(props.strikethrough),
          truncate: Boolean(props.noWrap),
          whiteSpace: getWhiteSpaceVariant(props),
          overflowWrap: props.overflowWrap ?? 'initial',
        }),
        TEXT_SIZE_CLASSES[size ?? 'base'],
        isNil(align) ? undefined : TEXT_ALIGN_CLASSES[align],
        props.className
      )}
      style={{color: getTextColorByPath(props.color ?? 'primary', theme)}}
      title={props.title}
    >
      {props.children}
    </div>
  );
});
